import { SPACE } from '@angular/cdk/keycodes';
import { KeyValuePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { map } from 'rxjs';

import { DemoAutocompleteFields } from '@demo/data/demo-cipo-fields';
import { GenericObjectType } from '@models/helpers';
import { CipoAutocompleteComponent } from '@shared/components/fields/cipo-autocomplete/cipo-autocomplete.component';
import { CipoListControl } from '@shared/components/fields/common';
import { CipoFormModule } from '@shared/modules';

@Component({
  selector: 'cipo-demo-cipo-autocomplete',
  imports: [CipoAutocompleteComponent, KeyValuePipe, CipoFormModule, MatAutocompleteModule],
  templateUrl: './demo-cipo-autocomplete.component.html',
})
export class DemoCipoAutocompleteComponent {
  fb = inject(FormBuilder);

  form = signal(new FormGroup<{ [x: string]: CipoListControl }>({}));

  constructor() {
    DemoAutocompleteFields.forEach(field => {
      const { name, defaultValue, ...props } = field;
      const control = new CipoListControl(defaultValue, props);
      this.form().addControl(name, control);
    });
  }

  //   Formula autocomplete playground
  formulaAutocomplete = new FormControl<string[]>([]);
  formulaOptions = [
    { key: '#initialValue', value: 'Initial value' },
    { key: '#changeOrder', value: 'Change order' },
    { key: '#revisedValue', value: 'Revised value' },
    { key: '#totalWorkers', value: 'Total workers' },
    { key: '#workDays', value: 'Work days' },
    { key: '#dailyRevenue', value: 'Daily revenue' },
    { key: '#finalValue', value: 'Final value' },
    { key: ':+', value: '+' },
    { key: ':-', value: '-' },
    { key: ':*', value: '*' },
    { key: ':/', value: '/' },
    { key: ':(', value: '(' },
    { key: ':)', value: ')' },
  ];

  // Key codes for chip input. Add ENTER if needed
  readonly separatorKeysCodes = [SPACE];
  loadingOptions = signal(false);
  searchCriteria = signal('');
  private optionsChanged = signal<string | null>(null);

  readonly formulaValue$ = this.formulaAutocomplete.valueChanges.pipe(
    map(values => values.map((value: string) => value.replace(/[:#]/g, '')).join(' ')),
  );

  readonly operators = this.formulaOptions.filter(option => option.key.startsWith(':')).map(option => option.value);

  optionsDict = this.formulaOptions.reduce((acc, option) => {
    acc[option.key] = option.value;
    return acc;
  }, {} as GenericObjectType);

  availableOptions = computed(() => {
    // Recompute options only when options or search criteria change
    this.optionsChanged();
    const search = this.searchCriteria().toLowerCase();
    return this.formulaOptions.filter(
      ({ key, value }) => value.toLowerCase().includes(search) || key.toLowerCase().includes(search),
    );
  });

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value || (Number.isNaN(Number(value)) && !this.operators.includes(value))) return;

    const controlValue: string[] = this.formulaAutocomplete.value || [];

    this.formulaAutocomplete.patchValue([...controlValue, value]);
    event.chipInput!.clear();
    this.searchCriteria.set('');
  }

  remove(option: string): void {
    const currentValue = this.formulaAutocomplete.value || [];
    this.formulaAutocomplete.setValue(currentValue.filter(item => item !== option));
    this.optionsChanged.set(new Date().toISOString());
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    // Be aware if you add enter that selecting a option will also add the searchString as a separate chip
    // To avoid it - add a 'optionSelected = true' and a setTimeout(10ms) in this.add method
    const currentValue = this.formulaAutocomplete.value || [];
    this.formulaAutocomplete.patchValue([...currentValue, event.option.value]);
    this.searchCriteria.set('');
    this.optionsChanged.set(new Date().toISOString());
  }
}
